import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../App';

export function useWebSocket(url) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pollRef = useRef(null);
  const wsFailedRef = useRef(false);
  const lastPollTimeRef = useRef(new Date().toISOString());
  const maxEvents = 50;

  // Polling fallback when WebSocket is not available
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setConnected(true); // Show as connected since polling works
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get('/api/events/poll', {
          params: { since: lastPollTimeRef.current }
        });
        const data = res.data;
        if (data.has_updates) {
          const newEvents = [];
          (data.activities || []).forEach(act => {
            newEvents.push({ type: 'activity', ...act });
          });
          (data.notifications || []).forEach(notif => {
            newEvents.push({ type: 'notification', ...notif });
          });
          if (newEvents.length > 0) {
            setEvents(prev => [...newEvents, ...prev].slice(0, maxEvents));
            setLastEvent(newEvents[0]);
          }
        }
        lastPollTimeRef.current = data.server_time || new Date().toISOString();
      } catch {
        // Silently fail polling
      }
    }, 30000); // Poll every 30 seconds
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsFailedRef.current) return; // Don't retry WS if it failed, use polling

    try {
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        setConnected(true);
        wsFailedRef.current = false;
        // Auth with user email
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const user = JSON.parse(localStorage.getItem('sls_user') || '{}');
          if (user.email) {
            try {
              wsRef.current.send(JSON.stringify({ type: 'auth', email: user.email }));
            } catch {}
          }
        }
        // Stop polling if WS connects
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };

      wsRef.current.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          setLastEvent(data);
          setEvents(prev => [data, ...prev].slice(0, maxEvents));
        } catch {}
      };

      wsRef.current.onclose = () => {
        setConnected(false);
        // After 2 failed attempts, switch to polling
        if (wsFailedRef.current) {
          startPolling();
        } else {
          reconnectRef.current = setTimeout(connect, 5000);
        }
      };

      wsRef.current.onerror = () => {
        wsFailedRef.current = true;
        wsRef.current?.close();
      };
    } catch {
      wsFailedRef.current = true;
      startPolling();
    }
  }, [url, startPolling]);

  useEffect(() => {
    connect();
    // Start polling as immediate fallback after 3 seconds if WS hasn't connected
    const fallbackTimer = setTimeout(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        wsFailedRef.current = true;
        startPolling();
      }
    }, 3000);

    return () => {
      clearTimeout(fallbackTimer);
      clearTimeout(reconnectRef.current);
      clearInterval(pollRef.current);
      wsRef.current?.close();
    };
  }, [connect, startPolling]);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return { connected, lastEvent, events, send };
}
